'use client';

import { SectionCard, SectionParagraph, SectionList, parseTextToList } from './section-card';
import { OrderedStepList } from './stage-flow';
import type { DefinitionUseCase } from '@/hooks/use-definition';

interface UseCaseTemplateProps {
  useCase: DefinitionUseCase;
}

export function UseCaseTemplate({ useCase }: UseCaseTemplateProps) {
  const preconditionsList = parseTextToList(useCase.preconditions);
  const postconditionsList = parseTextToList(useCase.postconditions);
  const businessRulesList = parseTextToList(useCase.businessRules);

  return (
    <div className="space-y-6">
      {/* Description */}
      {useCase.description && (
        <SectionCard title="Description">
          <SectionParagraph text={useCase.description} />
        </SectionCard>
      )}

      {/* Preconditions */}
      {preconditionsList.length > 0 && (
        <SectionCard title="Preconditions">
          <SectionList items={preconditionsList} />
        </SectionCard>
      )}

      {/* Main Flow */}
      {Array.isArray(useCase.mainFlow) && useCase.mainFlow.length > 0 && (
        <SectionCard title="Main Flow">
          <OrderedStepList steps={useCase.mainFlow as ({ step?: number; action?: string; description?: string }[] | string[])} />
        </SectionCard>
      )}

      {/* Exception Handling */}
      {Array.isArray(useCase.exceptionFlows) && useCase.exceptionFlows.length > 0 && (
        <SectionCard title="Exception Handling">
          <div className="space-y-4">
            {useCase.exceptionFlows.map((flow, i) => {
              if (typeof flow === 'string') {
                return (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                    <span>{flow}</span>
                  </div>
                );
              }
              const ef = flow as { name?: string; trigger?: string; recovery?: string; steps?: string[] };
              return (
                <div key={i} className="bg-amber-50/50 border border-amber-200 rounded-lg p-4">
                  {ef.name && <h4 className="font-medium text-gray-900 text-sm mb-1">{ef.name}</h4>}
                  {ef.trigger && (
                    <p className="text-sm text-amber-700 mb-2">
                      <span className="font-medium">Trigger:</span> {ef.trigger}
                    </p>
                  )}
                  {ef.recovery && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-700">Recovery:</span> {ef.recovery}
                    </p>
                  )}
                  {Array.isArray(ef.steps) && ef.steps.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {ef.steps.map((step, si) => (
                        <li key={si} className="text-sm text-gray-600 flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                          {step}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Postconditions */}
      {postconditionsList.length > 0 && (
        <SectionCard title="Postconditions">
          <SectionList items={postconditionsList} />
        </SectionCard>
      )}

      {/* Business Rules */}
      {businessRulesList.length > 0 && (
        <SectionCard title="Business Rules">
          <SectionList items={businessRulesList} />
        </SectionCard>
      )}
    </div>
  );
}
